import type { PatternRunResult } from '../patterns';
import type { ManuscriptDocument } from '../pdf';
import type { ReviewerCandidate, ReferenceAuditResult, ReviewerSearchResult } from '../apis';

export type DecisionLabel =
  | 'desk_reject'
  | 'reject_with_feedback'
  | 'send_for_review'
  | 'send_for_review_after_revisions';

export const DECISION_LABELS: Record<DecisionLabel, string> = {
  desk_reject: 'Desk reject',
  reject_with_feedback: 'Reject with feedback',
  send_for_review: 'Send for review',
  send_for_review_after_revisions: 'Send for review after revisions',
};

/** Optional integrity signals the rubric folds in (P0-2). */
export interface DecisionInputs {
  referenceAudit?: ReferenceAuditResult | null;
}

const RANK: DecisionLabel[] = [
  'send_for_review',
  'send_for_review_after_revisions',
  'reject_with_feedback',
  'desk_reject',
];

interface DecisionBasis {
  missingRed: number;
  redIntegrity: number;
  amberIntegrity: number;
  retracted: number;
  refMismatch: number;
  lowConfidence: boolean;
  hasGuideline: boolean;
  anySignal: boolean;
}

function decisionBasis(
  analysis: PatternRunResult,
  opts: DecisionInputs | undefined,
): DecisionBasis {
  const m = analysis.methodology;
  const missingRed = m
    ? m.items.filter((i) => i.status === 'not_located' && i.severityIfMissing === 'red').length
    : 0;
  const integrity = analysis.findings.filter((f) => f.category !== 'methodology');
  const redIntegrity = integrity.filter((f) => f.severity === 'red').length;
  const amberIntegrity = integrity.filter((f) => f.severity === 'amber').length;
  const ra = opts?.referenceAudit;
  const retracted = ra ? ra.entries.filter((e) => e.outcome.status === 'retracted').length : 0;
  const refMismatch = ra ? ra.entries.filter((e) => e.outcome.status === 'mismatch').length : 0;
  const lowConfidence =
    analysis.classifier.label !== 'unclassified' && analysis.classifier.confidence < 0.4;
  const anySignal =
    Boolean(m) ||
    integrity.length > 0 ||
    Boolean(ra && ra.entries.length > 0) ||
    analysis.classifier.label !== 'unclassified';
  return {
    missingRed,
    redIntegrity,
    amberIntegrity,
    retracted,
    refMismatch,
    lowConfidence,
    hasGuideline: Boolean(m),
    anySignal,
  };
}

/**
 * Rubric-derived decision suggestion (advisory). Now folds *integrity*
 * signals in alongside methodology gaps (P0-2): a cited **retracted**
 * reference, fabricated/mismatched references, red statistical/voice
 * findings, and very-low study-type confidence all push the recommendation
 * toward more scrutiny. Highest-severity driver wins. The editor can always
 * override; "the editor's word is final".
 *
 * Returns null only when there is genuinely nothing to base a suggestion on
 * (unclassified, no methodology, no findings, no reference audit).
 */
export function suggestDecision(
  analysis: PatternRunResult,
  opts?: DecisionInputs,
): DecisionLabel | null {
  const b = decisionBasis(analysis, opts);
  // Preserve prior behaviour: a clean narrative review / unclassified with no
  // integrity signals yields no methodology-based suggestion.
  const noIntegrity =
    b.redIntegrity === 0 && b.retracted === 0 && b.refMismatch === 0 && !b.lowConfidence;
  if (
    (analysis.classifier.label === 'unclassified' || analysis.classifier.noFormalGuideline) &&
    noIntegrity
  ) {
    return null;
  }
  if (!b.anySignal) return null;

  // Methodology base. The pattern engine's `not_located` is a LOW-CONFIDENCE
  // signal: it routinely fails to match text that IS present (e.g. a real
  // manuscript's "Eligible participants were Omani adults aged ≥18…" does not
  // match an "inclusion criteria" locator; a full "Statistical analysis…
  // Spearman / logistic regression" section is missed by STROBE-12). The UI
  // itself tells the editor "not located ≠ missing". So pattern-only missing
  // items must NEVER, on their own, drive the strongest actions — a false
  // desk-reject on a sound paper is exactly the failure this engine must not
  // make. They cap at "send for review after revisions" (i.e. *verify these
  // items*). Reject / desk-reject must come from genuinely reliable signals
  // (retractions, red integrity), or — when available — LLM-confirmed
  // missing grades, not unreliable pattern locator misses.
  let rank = b.missingRed >= 1 ? 1 : 0;

  // Integrity escalations (take the most severe).
  if (b.retracted >= 3) rank = Math.max(rank, 3);
  if (b.retracted >= 1 || b.redIntegrity >= 2) rank = Math.max(rank, 2);
  if (
    b.redIntegrity === 1 ||
    b.refMismatch >= 3 ||
    b.amberIntegrity >= 4 ||
    b.lowConfidence
  )
    rank = Math.max(rank, 1);

  return RANK[rank];
}

/** One-line, plain-English basis for the suggestion — shown to the editor
 *  and printed in the report when no written rationale exists. */
export function explainDecision(
  analysis: PatternRunResult,
  opts?: DecisionInputs,
): string {
  const suggestion = suggestDecision(analysis, opts);
  if (!suggestion) {
    return 'No rubric suggestion — study type not classified to a formal guideline and no integrity flags. Editor to assess.';
  }
  const b = decisionBasis(analysis, opts);
  const drivers: string[] = [];
  if (b.missingRed > 0)
    drivers.push(
      `${b.missingRed} high-severity ${analysis.methodology?.guidelineId ?? 'checklist'} item${b.missingRed === 1 ? '' : 's'} not located`,
    );
  if (b.retracted > 0)
    drivers.push(`${b.retracted} cited reference${b.retracted === 1 ? '' : 's'} flagged RETRACTED`);
  if (b.refMismatch > 0)
    drivers.push(`${b.refMismatch} reference metadata mismatch${b.refMismatch === 1 ? '' : 'es'}`);
  if (b.redIntegrity > 0)
    drivers.push(`${b.redIntegrity} red integrity flag${b.redIntegrity === 1 ? '' : 's'}`);
  if (b.amberIntegrity > 0) drivers.push(`${b.amberIntegrity} amber integrity flag(s)`);
  if (b.lowConfidence)
    drivers.push(
      `study type uncertain (${Math.round(analysis.classifier.confidence * 100)}% confidence)`,
    );
  const basis = drivers.length ? drivers.join('; ') : 'no high-severity gaps or integrity flags detected';
  return `${DECISION_LABELS[suggestion]} suggested — ${basis}. Advisory; the editor's decision is final.`;
}

export interface EditorDraft {
  decision: DecisionLabel | null;
  rationale: string;
  editorNotes: string;
  /** Candidate keys the editor selected for the recommendation. */
  selectedReviewerKeys: string[];
  /** Manually-added reviewers (typed by editor). */
  manualReviewers: ReviewerCandidate[];
}

export interface ExportInput {
  manuscript: ManuscriptDocument;
  fileName: string;
  pattern: PatternRunResult;
  referenceAudit: ReferenceAuditResult | null;
  reviewers: ReviewerSearchResult | null;
  draft: EditorDraft;
  skimReportId: string;
  /** ISO string. */
  generatedAt: string;
}
